/**
 * External Google Maps navigation contract (AUTH56 Slice A).
 * Pure URL / eligibility helpers — no window.open, geolocation, Maps JS, or API keys.
 * Reused by Journey Today / Planning consumers (Slice B/C).
 */

import { normalizeCoordinatePair } from '../explore/socialMapCoordinate';

export const EXTERNAL_MAP_NAVIGATION_SOURCES = Object.freeze({
  COORDINATES: 'COORDINATES',
  NAME: 'NAME',
  ADDRESS: 'ADDRESS',
  LABEL: 'LABEL',
});

/** Suggested `<a>` attributes for Slice B/C (helper does not open windows). */
export const EXTERNAL_MAP_NAVIGATION_LINK = Object.freeze({
  target: '_blank',
  rel: 'noopener noreferrer',
});

const DIRECTIONS_BASE = 'https://www.google.com/maps/dir/';
const SEARCH_BASE = 'https://www.google.com/maps/search/';

/** Statuses that may expose external navigation when a destination resolves. */
const NAVIGABLE_STATUSES = Object.freeze([
  'PLANNED',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
]);

function trimText(value) {
  if (value == null) return '';
  return String(value).trim();
}

function firstNonBlank(...candidates) {
  for (const candidate of candidates) {
    const text = trimText(candidate);
    if (text) return text;
  }
  return '';
}

function serializeCoordinate(value) {
  // Stable decimal string; preserves valid 0 / 0.0 from normalizeCoordinatePair.
  return String(value);
}

function directionsUrl(latitude, longitude) {
  const destination = `${serializeCoordinate(latitude)},${serializeCoordinate(longitude)}`;
  const params = new URLSearchParams();
  params.set('api', '1');
  params.set('destination', destination);
  return `${DIRECTIONS_BASE}?${params.toString()}`;
}

function searchUrl(query) {
  const params = new URLSearchParams();
  params.set('api', '1');
  params.set('query', query);
  return `${SEARCH_BASE}?${params.toString()}`;
}

function unavailable() {
  return Object.freeze({
    available: false,
    href: null,
    source: null,
  });
}

/**
 * Resolve an external Maps destination from a normalized activity (or activity-like) shape.
 * Destination priority (audited contract):
 * 1. valid coordinate pair on activity.location
 * 2. location.name
 * 3. location.address (defensive; schedule enricher often supplies null today)
 * 4. activity.locationLabel
 *
 * Does not consult activity status — use isExternalMapNavigationStatusEligible /
 * isActivityExternalMapNavigationEligible for UI policy.
 *
 * @returns {{ available: boolean, href: string | null, source: string | null }}
 */
export function resolveExternalMapNavigation(activity) {
  if (!activity || typeof activity !== 'object') {
    return unavailable();
  }

  const location = activity.location && typeof activity.location === 'object'
    ? activity.location
    : null;
  const coords = normalizeCoordinatePair(location || {});
  if (coords.markerAvailable) {
    return Object.freeze({
      available: true,
      href: directionsUrl(coords.latitude, coords.longitude),
      source: EXTERNAL_MAP_NAVIGATION_SOURCES.COORDINATES,
    });
  }

  const name = firstNonBlank(location?.name);
  if (name) {
    return Object.freeze({
      available: true,
      href: searchUrl(name),
      source: EXTERNAL_MAP_NAVIGATION_SOURCES.NAME,
    });
  }

  const address = firstNonBlank(location?.address);
  if (address) {
    return Object.freeze({
      available: true,
      href: searchUrl(address),
      source: EXTERNAL_MAP_NAVIGATION_SOURCES.ADDRESS,
    });
  }

  const label = firstNonBlank(activity.locationLabel);
  if (label) {
    return Object.freeze({
      available: true,
      href: searchUrl(label),
      source: EXTERNAL_MAP_NAVIGATION_SOURCES.LABEL,
    });
  }

  return unavailable();
}

/**
 * Status-only eligibility (no permission / role checks).
 * CANCELLED and unknown statuses are not eligible.
 */
export function isExternalMapNavigationStatusEligible(activityStatus) {
  return NAVIGABLE_STATUSES.includes(String(activityStatus || ''));
}

/**
 * UI policy: status eligible AND destination resolvable.
 * URL construction remains independent of status.
 */
export function isActivityExternalMapNavigationEligible(activity) {
  if (!isExternalMapNavigationStatusEligible(activity?.status)) {
    return false;
  }
  return resolveExternalMapNavigation(activity).available;
}
